import {
    Component,
    Directive,
    EventEmitter,
    inject,
    Input,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { catchError, combineLatest, Subscription, switchMap, take, tap } from 'rxjs';
import { AccessTypeSelection } from '../../../../../common/enums/access-type-selection.enum';
import { ContentUtil } from '../../../../utils/content/content.util';
import conentFormatUtils from '../../../../utils/content/content-format.util';
import { getAdobeConfigData } from './grantee-authorization-agreement-adobe';
import { AdobeTagging } from '../../../../utils/decorators/dev/adobe-launch';
import { AccessPermissionsService } from '../../../../services/access-permissions/access-permissions.service';
import { TOKEN_PARAM } from '../../../../constants/application-routes';
import { ActivatedRoute } from '@angular/router';
import { ErrorService } from '../../../../services/generic-error/error.service';
import { ErrorPage, errorPageMap } from '../../../../services/generic-error/error-page-utils';
import {
    AcceptPermissionResponseDTO,
    BulkAcceptPermissionResponseDTO,
} from '../../../../../common/dtos/access-permissions.dto';
import { AdobeGranteeFlowType } from '../grantee-flows.interface';
import { Store } from '@ngrx/store';
import {
    selectGranteeRegulatoryQuestions,
    selectIsMultiAccount,
} from '../../../../store/selectors/grantee.selector';
import { selectUserInfo } from '../../../../store/selectors/account-permissions.selector';
import {
    setAcceptPermissionResult,
    setBulkAcceptResponse,
} from '../../../../store/actions/grantee-flow.actions';
import { PERMISSION_REQUEST_ERRORS } from '../../../../enums/permission_request_error.enum';
import { LoggerService } from '../../../../services/logger/logger.service';

@Directive()
class GranteeAuthorizationAgreementComponent implements OnInit, OnDestroy {
    readonly accessTypeSelection = AccessTypeSelection;
    @Input() nextStepClicked: EventEmitter<any> = new EventEmitter<any>();
    @Input() nextStepReady: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Input() locationChanged: EventEmitter<any>;
    granteeAuthorizationAgreement = ContentUtil.granteeFragmentsContent.trmAuthorizationAgreement;
    private accessPermissionsService = inject(AccessPermissionsService);
    private route = inject(ActivatedRoute);
    private errorService = inject(ErrorService);
    private store = inject(Store);
    private logger = inject(LoggerService);
    private subs: Subscription = new Subscription();

    ngOnInit(): void {
        this.granteeAuthorizationAgreement.text1 = conentFormatUtils.addClassToLinks(
            this.granteeAuthorizationAgreement.text1,
        );

        this.subs.add(this.nextStepClicked.subscribe(this.onNextStepLoad.bind(this)));
        this.scrollTop();
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }

    private onNextStepLoad(): void {
        const token = this.route.snapshot.params[TOKEN_PARAM];
        this.subs.add(
            combineLatest([
                this.store.select(selectGranteeRegulatoryQuestions),
                this.store.select(selectIsMultiAccount),
                this.store.select(selectUserInfo),
            ])
                .pipe(
                    take(1),
                    switchMap(([granteeRegulatoryQuestions, isMultiAccount, granteeInfo]) =>
                        (isMultiAccount
                            ? this.accessPermissionsService.bulkAcceptPermission(
                                  token,
                                  granteeRegulatoryQuestions,
                              )
                            : this.accessPermissionsService.acceptPermission(
                                  token,
                                  granteeRegulatoryQuestions,
                              )
                        ).pipe(
                            tap((response: AcceptPermissionResponseDTO | BulkAcceptPermissionResponseDTO) => {
                                const isFullRegError =
                                    response.error === PERMISSION_REQUEST_ERRORS.FULL_REG_ERROR;
                                const isNoError = response.error === PERMISSION_REQUEST_ERRORS.NO_ERROR;

                                this.store.dispatch(
                                    setAcceptPermissionResult({
                                        hasFullRegError: isFullRegError,
                                    }),
                                );

                                if (isFullRegError || isNoError) {
                                    if ('results' in response && response.results) {
                                        const allAccountsFailed = response.results.every(
                                            (r) => !r.confirmationNumber,
                                        );
                                        const hasPartialFailure = response.results.some(
                                            (r) => !r.confirmationNumber,
                                        );

                                        if (allAccountsFailed) {
                                            this.logger.info(
                                                `[GranteeAuthorizationAgreement] Navigating to ACCEPT_ACCESS_FLOW_SUBMIT_ALL_FAILED. ` +
                                                `Grantee POID: ${granteeInfo?.clientPoid}, ` +
                                                `Total results: ${response.results?.length ?? 0}, Successful: 0, Failed: ${response.results?.length ?? 0}` +
                                                `Access level: LIMITED`,
                                            );
                                            this.errorService.showError(
                                                ErrorPage.ACCEPT_ACCESS_FLOW_SUBMIT_ALL_FAILED,
                                            );
                                            this.nextStepReady.emit(false);
                                            return;
                                        }

                                        if (hasPartialFailure) {
                                            const failedCount = response.results.filter((r) => !r.confirmationNumber).length;
                                            const successCount = response.results.length - failedCount;
                                            this.logger.info(
                                                `[GranteeAuthorizationAgreement] Navigating to ACCEPT_ACCESS_FLOW_PARTIAL_SUCCESS. ` +
                                                `Grantee POID: ${granteeInfo?.clientPoid}, ` +
                                                `Total results: ${response.results?.length ?? 0}, Successful: ${successCount}, Failed: ${failedCount}` +
                                                `Access level: LIMITED`,
                                            );
                                            this.store.dispatch(
                                                setBulkAcceptResponse({
                                                    response: response as BulkAcceptPermissionResponseDTO,
                                                }),
                                            );
                                            this.nextStepReady.emit(false);
                                            this.errorService.showError(
                                                ErrorPage.ACCEPT_ACCESS_FLOW_PARTIAL_SUCCESS,
                                            );
                                            return;
                                        }
                                    }

                                    this.logger.info(
                                        `[GranteeAuthorizationAgreement] Navigating to AAF_ALL_SUCCESS page. ` +
                                        `Grantee POID: ${granteeInfo?.clientPoid}` +
                                        `Access level: LIMITED`,
                                    );
                                    this.nextStepReady.emit(true);
                                } else {
                                    this.logger.info(
                                        `[GranteeAuthorizationAgreement] Error response received. ` +
                                        `Grantee POID: ${granteeInfo?.clientPoid}, Error: ${response.error}` +
                                        `Access level: LIMITED`,
                                    );
                                    this.nextStepReady.emit(false);
                                    this.errorService.showError(errorPageMap[response.error]);
                                }
                            }),
                            catchError(() => {
                                this.logger.info('[GranteeAuthorizationAgreement] Technical error occurred during accept.');
                                this.errorService.showError(ErrorPage.TECH_DIFF);
                                return Promise.reject();
                            }),
                        ),
                    ),
                )
                .subscribe(),
        );
    }

    private scrollTop(): void {
        window.scrollTo(0, 0);
    }
}

@AdobeTagging(getAdobeConfigData(AdobeGranteeFlowType.EXISTING_LIMITED))
@Component({
    selector: 'zci-existing-grantee-authorization-agreement',
    templateUrl: 'grantee-authorization-agreement.component.html',
    styleUrls: ['grantee-authorization-agreement.component.scss'],
    standalone: true,
    providers: [ErrorService, AccessPermissionsService],
})
export class ExistingGranteeAuthorizationAgreementComponent extends GranteeAuthorizationAgreementComponent {}

@AdobeTagging(getAdobeConfigData(AdobeGranteeFlowType.NEW_LIMITED))
@Component({
    selector: 'zci-new-grantee-authorization-agreement',
    templateUrl: 'grantee-authorization-agreement.component.html',
    styleUrls: ['grantee-authorization-agreement.component.scss'],
    standalone: true,
    providers: [ErrorService, AccessPermissionsService],
})
export class NewGranteeAuthorizationAgreementComponent extends GranteeAuthorizationAgreementComponent {}

@AdobeTagging(getAdobeConfigData(AdobeGranteeFlowType.NON_VG_CLIENT_LIMITED))
@Component({
    selector: 'zci-new-grantee-authorization-agreement',
    templateUrl: 'grantee-authorization-agreement.component.html',
    styleUrls: ['grantee-authorization-agreement.component.scss'],
    standalone: true,
    providers: [ErrorService, AccessPermissionsService],
})
export class NonVgClientGranteeAuthorizationAgreementComponent extends GranteeAuthorizationAgreementComponent {}
